import { useId, useState } from 'react';
import type { DemandeSuivi, StatutDocument, SuiviDocument } from '../../lib/useDocuments';
import type { VueConvention } from '../../lib/useConventions';

const ACTIONS: Record<Exclude<StatutDocument, 'GENERE'>, string> = {
  VERIFIE: 'Marquer vérifié',
  VALIDE: 'Valider',
  TRANSMIS: 'Transmettre…',
};

/** Destinataires courants, proposés sans être imposés : la saisie reste libre. */
const DESTINATAIRES_SUGGERES = ['Apprenant', 'Employeur', 'OPCO', 'Représentant légal'];

const CLASSE_BOUTON =
  'inline-flex min-h-[36px] items-center justify-center rounded-lg border border-codialis-navy/20 px-3 py-1.5 text-[11px] font-semibold text-codialis-navy transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]';

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

function par(auteur: string | null): string {
  return auteur ? ` par ${auteur}` : '';
}

/** « Vérifié le 14/10/2026 par Alix Grondin · Validé le … » : ce qui a été franchi, et par qui. */
export function historiqueSuivi(suivi: SuiviDocument): string[] {
  return [
    suivi.verifieLe ? `Vérifié le ${jour(suivi.verifieLe)}${par(suivi.verifiePar)}` : null,
    suivi.valideLe ? `Validé le ${jour(suivi.valideLe)}${par(suivi.validePar)}` : null,
    suivi.transmisLe
      ? `Transmis le ${jour(suivi.transmisLe)}${suivi.destinataire ? ` à ${suivi.destinataire}` : ''}${par(suivi.transmisPar)}`
      : null,
  ].filter((ligne): ligne is string => ligne !== null);
}

/** Les étapes d'une convention se disent autrement : soumise, validée par la direction (TF-430). */
export function historiqueConvention(suivi: SuiviDocument, convention: VueConvention): string[] {
  return [
    convention.soumission
      ? `Soumise le ${jour(convention.soumission.le)}${par(convention.soumission.par)}`
      : null,
    convention.validation
      ? `Validée le ${jour(convention.validation.le)}${par(convention.validation.par)}`
      : null,
    suivi.transmisLe
      ? `Transmise le ${jour(suivi.transmisLe)}${suivi.destinataire ? ` à ${suivi.destinataire}` : ''}${par(suivi.transmisPar)}`
      : null,
  ].filter((ligne): ligne is string => ligne !== null);
}

/**
 * Suivi Vérifié / Validé / Transmis d'un document (CDC §6.4), commun au dossier
 * apprenant et aux documents pédagogiques.
 *
 * L'écran ne propose que l'étape suivante que l'API annonce (`etapes`) : le
 * suivi ne revient pas en arrière, et la règle vit côté serveur. Une
 * transmission demande son destinataire avant de partir.
 */
export function SuiviDocumentaire({
  suivi,
  peutAvancer,
  enCours,
  erreur,
  onAvancer,
  historique: historiqueImpose,
}: {
  suivi: SuiviDocument;
  peutAvancer: boolean;
  enCours: boolean;
  erreur: string | null;
  onAvancer: (demande: Omit<DemandeSuivi, 'documentId'>) => void;
  /** Étapes déjà franchies, dites autrement que par défaut (convention). */
  historique?: string[];
}): JSX.Element | null {
  const [transmission, setTransmission] = useState(false);
  const [destinataire, setDestinataire] = useState('');
  const idListe = `${useId()}-destinataires`;

  const historique = historiqueImpose ?? historiqueSuivi(suivi);
  const suivante = peutAvancer
    ? (suivi.etapes[0] as Exclude<StatutDocument, 'GENERE'> | undefined)
    : undefined;

  if (historique.length === 0 && suivante === undefined && !erreur) return null;

  return (
    <div className="mt-2 space-y-2">
      {historique.length > 0 && (
        <p className="text-[11px] text-codialis-gray">{historique.join(' · ')}</p>
      )}

      {suivante !== undefined &&
        (transmission ? (
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              onAvancer({ statut: 'TRANSMIS', destinataire: destinataire.trim() });
            }}
          >
            <label className="min-w-0 flex-1 basis-48">
              <span className="sr-only">Destinataire de la transmission</span>
              <input
                value={destinataire}
                onChange={(evenement) => setDestinataire(evenement.target.value)}
                list={idListe}
                placeholder="Destinataire (OPCO, employeur…)"
                maxLength={120}
                className="min-h-[36px] w-full rounded-lg border border-gray-200 bg-white px-3 text-xs text-codialis-navy focus:border-codialis-sky focus:outline-none [@media(pointer:coarse)]:min-h-[40px]"
              />
              <datalist id={idListe}>
                {DESTINATAIRES_SUGGERES.map((valeur) => (
                  <option key={valeur} value={valeur} />
                ))}
              </datalist>
            </label>
            <button
              type="submit"
              disabled={enCours || destinataire.trim() === ''}
              className={`${CLASSE_BOUTON} bg-codialis-navy text-white hover:bg-codialis-deep`}
            >
              Confirmer la transmission
            </button>
            <button
              type="button"
              onClick={() => setTransmission(false)}
              disabled={enCours}
              className={CLASSE_BOUTON}
            >
              Annuler
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() =>
              suivante === 'TRANSMIS' ? setTransmission(true) : onAvancer({ statut: suivante })
            }
            disabled={enCours}
            className={CLASSE_BOUTON}
          >
            {ACTIONS[suivante]}
          </button>
        ))}

      {erreur && (
        <p role="alert" className="text-[11px] text-red-700">
          {erreur}
        </p>
      )}
    </div>
  );
}
